// ConfirmDialog — общий диалог подтверждения вместо системного window.confirm.
// Системный диалог рисует ОС: он игнорирует тёмную тему и оформление приложения,
// а на необратимых действиях («снять доверенные устройства») это ещё и не даёт
// развернуть последствия текстом.
//
// Тема. Компонент НЕ опирается на переменные `--ai-*`: они объявлены в
// src/styles/injections-theme-css.js, а в App.jsx этот CSS рендерится только внутри
// сплэша (`if (!loaded)`) — страница Injections потому и переинжектит его сама.
// Диалог общий, звать его будут откуда угодно, поэтому он везёт свой CSS.
//
// Светлый/тёмный переключается порталом, а не пропом: портал целится в
// #blitz-theme-root — тот самый div, на котором висит класс .dark-mode, и диалог
// наследует палитру оттуда. Узел создаётся в App() СНАРУЖИ <AppInner/>, а все ранние
// return (сплэш, login, admin, security, botmessages) сидят ВНУТРИ AppInner — цель
// портала есть на любом экране, админские не исключение. Поэтому на белой
// SecurityPanel диалог в тёмной теме тоже тёмный.
// 2026-08-18: прежняя редакция абзаца утверждала обратное («на админках узла нет,
// портал уходит в body, диалог остаётся светлым») — неверно с первого дня.
// `|| document.body` ниже — аварийный путь для DOM без обёртки (тесты, песочницы),
// а не путь админок. Структуру держат ConfirmDialog.theme.test.jsx и App.test.jsx.
import { useEffect } from 'react';
import { createPortal } from 'react-dom';

const CONFIRM_DIALOG_CSS = `
.bcd-overlay {
  position: fixed; inset: 0; z-index: 1200;
  display: flex; align-items: center; justify-content: center; padding: 20px;
  background: rgba(15, 23, 42, 0.55); backdrop-filter: blur(3px);
  animation: bcdFade 0.12s ease;
}
.bcd-box {
  --bcd-surface: #FFFFFF;
  --bcd-text: #0F172A;
  --bcd-text-dim: #64748B;
  --bcd-cancel-bg: #F1F5F9;
  --bcd-cancel-text: #0F172A;
  --bcd-accent: #0EA5E9;
  --bcd-danger: #EF4444;
  --bcd-shadow: 0 20px 60px rgba(15, 23, 42, 0.25);
  box-sizing: border-box;
  width: 430px; max-width: 100%; padding: 24px;
  background: var(--bcd-surface); border-radius: 16px; box-shadow: var(--bcd-shadow);
  font-family: 'Plus Jakarta Sans', 'Segoe UI', sans-serif;
  animation: bcdRise 0.14s ease;
}
.dark-mode .bcd-box {
  --bcd-surface: #181D26;
  --bcd-text: #E8ECF2;
  --bcd-text-dim: #8C95A2;
  --bcd-cancel-bg: #232A35;
  --bcd-cancel-text: #E8ECF2;
  --bcd-accent: #38BDF8;
  --bcd-danger: #F0726E;
  --bcd-shadow: 0 20px 60px rgba(0, 0, 0, 0.55);
}
.bcd-title { font-size: 15px; font-weight: 700; color: var(--bcd-text); }
/* pre-line: сообщения приходят с \\n\\n — абзацы, а не одна простыня. */
.bcd-message {
  margin-top: 9px; font-size: 12.5px; line-height: 1.5;
  color: var(--bcd-text-dim); white-space: pre-line;
}
.bcd-btns { display: flex; justify-content: flex-end; gap: 8px; margin-top: 20px; }
.bcd-btn {
  padding: 7px 16px; border: none; border-radius: 11px;
  font-family: inherit; font-size: 13px; font-weight: 600; cursor: pointer;
}
.bcd-btn-cancel { background: var(--bcd-cancel-bg); color: var(--bcd-cancel-text); }
.bcd-btn-ok { padding: 7px 20px; background: var(--bcd-accent); color: #FFFFFF; }
.bcd-btn-ok[data-danger="true"] { background: var(--bcd-danger); }
.bcd-btn:focus-visible { outline: 2px solid var(--bcd-accent); outline-offset: 2px; }
@keyframes bcdFade { from { opacity: 0; } }
@keyframes bcdRise { from { opacity: 0; transform: translateY(8px); } }
@media (prefers-reduced-motion: reduce) {
  .bcd-overlay, .bcd-box { animation: none; }
}
`;

function injectCss() {
  if (typeof document === 'undefined' || document.getElementById('blitz-confirm-dialog-css')) return;
  const el = document.createElement('style');
  el.id = 'blitz-confirm-dialog-css';
  el.textContent = CONFIRM_DIALOG_CSS;
  document.head.appendChild(el);
}

/**
 * @param {object}   p
 * @param {string}   p.title         — заголовок, короткая формулировка действия
 * @param {string}  [p.message]      — последствия; \n\n разбивает на абзацы
 * @param {boolean} [p.danger]       — деструктив: кнопка подтверждения красная
 * @param {string}  [p.confirmLabel]
 * @param {string}  [p.cancelLabel]
 * @param {() => void} p.onConfirm
 * @param {() => void} p.onCancel    — Escape и клик по подложке зовут его же
 */
export function ConfirmDialog({
  title, message, danger = false,
  confirmLabel = 'Подтвердить', cancelLabel = 'Отмена',
  onConfirm, onCancel,
}) {
  injectCss();

  // Escape слушаем на документе, а не на подложке: onKeyDown на div сработает
  // только когда фокус уже внутри, и на «просто открыли диалог» Escape молчит.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return createPortal(
    <div
      className="bcd-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div
        className="bcd-box"
        data-testid="confirm-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="bcd-title">{title}</div>
        {message && <div className="bcd-message">{message}</div>}
        <div className="bcd-btns">
          {/* Аудит DC 15.10 (#49): в danger фокус на «Отмена» — случайный Enter сразу после открытия не удаляет. */}
          <button type="button" className="bcd-btn bcd-btn-cancel" data-testid="confirm-cancel" autoFocus={danger} onClick={onCancel}>
            {cancelLabel}
          </button>
          <button
            type="button"
            className="bcd-btn bcd-btn-ok"
            data-testid="confirm-ok"
            data-danger={danger ? 'true' : 'false'}
            autoFocus={!danger}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.getElementById('blitz-theme-root') || document.body,
  );
}

export default ConfirmDialog;
